//
// ExtendedDefinitionMenu for RecordInspector
//
// Per-record extension actions: start / change to / drop.
//

import { observer } from 'mobx-react-lite'
import { useStore } from '../../../stores'

import { FixedVSpace, VScrollView, VView } from '../../../appview'
import {
    Header,
    Icon,
    Item,
    Menu,
    MenuDivider,
    MenuItem,
    Text,
} from '../../../components'
import { RecordExtensionPickPanel, RecordExtensionStartPanel } from '../../../panels'
import { changeDefinitionLostDataFields } from '../../../stores/data/utils'
import { ItemTitle } from '../../components'

const ConfirmDropSubmenu = observer(function ConfirmDropSubmenu({
    lostFields,
    onConfirm,
    onCancel,
}) {
    const { app } = useStore()

    return (
        <VView className="actions-panel recorddefinition-revert-panel">
            <div className="inspector">
                <Header>{app.text('Drop this record extension')}</Header>
                <Header className="cc-danger">{app.text('Warning')}</Header>
                <Text>
                    {app.text(
                        `Removing the extension can result in data loss
                         when fields are no longer available in the original
                         definition. This data cannot be recovered by
                         re-adding the fields.`
                    )}
                </Text>
                {lostFields.length ? (
                    <>
                        <Text>
                            {app.text(`For this record, these fields will be lost:`)}
                        </Text>
                        <VScrollView className="lost-fields">
                            {lostFields.map(field => (
                                <Item key={field.gid}>
                                    <Icon name="field" size="text" />{' '}
                                    <ItemTitle item={field} ellipsisAfter={32} />
                                </Item>
                            ))}
                        </VScrollView>
                        <FixedVSpace size={5} />
                    </>
                ) : undefined}
            </div>
            <Menu>
                <MenuItem onClick={onCancel}>{app.text('Cancel')}</MenuItem>
                <MenuItem className="cc-danger" onClick={onConfirm}>
                    {app.text('Yes, drop this extension')}
                </MenuItem>
            </Menu>
        </VView>
    )
})

export const ExtendedDefinitionMenu = observer(function ExtendedDefinitionMenu({
    worksheet,
    onAction,
}) {
    const { app, data } = useStore()

    const _onAction = (action, parameter) => {
        onAction && onAction(action, parameter)
    }

    const record = worksheet.record
    const definition = data.definitions.get(record.definition)
    const isExtended = definition && definition.is_extended
    const original_definition =
        definition && definition.is_extended
            ? data.definitions.get(definition.original)
            : null

    const lostFields =
        original_definition && definition
            ? changeDefinitionLostDataFields(original_definition, definition, data)
            : []

    if (record.is_link) {
        return (
            <VView className="actions-panel recorddefinition-change-panel">
                <div className="inspector">
                    <Text>
                        {app.text(
                            `Extension changes are not available for linked records.`
                        )}
                    </Text>
                </div>
                <Menu>
                    <MenuItem onClick={() => _onAction('cancel')}>
                        {app.text('Ok')}
                    </MenuItem>
                </Menu>
            </VView>
        )
    }

    return (
        <Menu>
            <MenuItem
                submenu={
                    <RecordExtensionStartPanel
                        worksheet={worksheet}
                        onStart={name => _onAction('start_extension', name)}
                        onCancel={() => _onAction('cancel')}
                    />
                }
            >
                {app.text('Start new extension …')}
            </MenuItem>
            <MenuItem
                submenu={
                    <RecordExtensionPickPanel
                        worksheet={worksheet}
                        title={app.text('Change extension')}
                        description={app.text(
                            'Change this record extension to another sibling extension or base definition.'
                        )}
                        excludeCurrent={true}
                        confirmLabel={app.text('Yes, change this definition')}
                        onPick={definition_gid =>
                            _onAction('change_extension', definition_gid)
                        }
                        onCancel={() => _onAction('cancel')}
                    />
                }
            >
                {app.text('Change to …')}
            </MenuItem>
            <MenuDivider />
            <MenuItem
                disabled={!isExtended}
                className={lostFields.length ? 'cc-notice' : ''}
                submenu={
                    isExtended ? (
                        <ConfirmDropSubmenu
                            lostFields={lostFields}
                            onConfirm={() => _onAction('drop_extension')}
                            onCancel={() => _onAction('cancel')}
                        />
                    ) : undefined
                }
            >
                {app.text('Drop extension …')}
            </MenuItem>
        </Menu>
    )
})
